import { createContext, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useGetGQLClientForProject } from "hooks/init-gql-client";

import jwtDecode from "jwt-decode";
import { NotFoundPage } from "pages";
import moment from "moment";
import { message } from "antd";
import { simplifyProjectIntegrationConfigs } from "pages/subscription-settings/integrations-tab-new/utils";
import useChangeEventPolling, {
  TAuditChangeLog
} from "change-events/change-event-polling";
import { useProjectGlobalDataSource } from "hooks/project-global-data";
import { isPermissionNotGranttedUtil } from "utils/permissions";
import { getHasuraClaims } from "utils/jwt-utils";
import { useUserProjectProfileInfo } from "hooks/users";
import { EUserRoleName, EUserTypes, ErrorMessages } from "../constants";

export interface ProjectProviderProps {
  children: React.ReactNode;
}

export type ProjectSettingType = {
  id: string;
  mode: number;
  spec_section: any;
  name: string;
  timezone: {
    timezone_id: string;
    name: string;
  };
  new_entity_model_enabled?: boolean;
};

export type TProjectContext = {
  projectToken: any;
  gqlClientForProject: any;
  tokenRetrievalState: any;
  tokenContents?: {
    userType: string;
    role: string;
    claims: any;
  };
  calHolidaysWeekends?: { holidays: Array<any>; weekends: Array<any> };
  columnHeaders: {
    submittalHeaders: any;
    materialHeaders: any;
    submittalHeaderMap: any;
    materialHeaderMap: any;
    bidPackageHeaders: any;
    designPackageHeaders: any;
    bidPackageHeaderMap: any;
    designPackageHeaderMap: any;
  };
  projectDetails: ProjectSettingType | undefined;
  projectIntegrationConfig: any;
  eventLogs: Array<TAuditChangeLog>;
  lastImportLogData: any;
  isFeatureFlagEnabled: (feature: string) => Boolean;
  isProductEntitlementsDataAvailable: boolean;
  isProjectMember?: boolean;
  isProcoreProject?: boolean;
  loggedInUserProjectProfileResponse?: any;
  metadata?: any;
};

export const ProjectContext = createContext<TProjectContext>({
  projectToken: null,
  gqlClientForProject: null,
  tokenRetrievalState: null,
  tokenContents: undefined,
  calHolidaysWeekends: { holidays: [], weekends: [] },
  columnHeaders: {
    submittalHeaders: null,
    materialHeaders: null,
    submittalHeaderMap: {},
    materialHeaderMap: {},
    bidPackageHeaders: null,
    designPackageHeaders: null,
    bidPackageHeaderMap: {},
    designPackageHeaderMap: {}
  },
  projectDetails: undefined,
  projectIntegrationConfig: null,
  eventLogs: [],
  lastImportLogData: null,
  isFeatureFlagEnabled: () => false,
  isProductEntitlementsDataAvailable: false,
  isProjectMember: false,
  isProcoreProject: false,
  loggedInUserProjectProfileResponse: {
    loading: false,
    error: null,
    data: undefined
  },
  metadata: {}
});

const getUserTypeFromToken = (token: string) => {
  const hasuraClaims = getHasuraClaims(token);
  return (
    hasuraClaims?.["x-hasura-user-type"] ||
    EUserTypes[EUserTypes.GENERAL_CONTRACTOR]
  );
};

const getProjectRoleFromToken = (token: string) => {
  const hasuraClaims = getHasuraClaims(token);
  return (
    hasuraClaims?.["x-hasura-default-role"] ||
    EUserRoleName[EUserRoleName.owner]
  );
};

const isCurrentProjectMember = (token: string) => {
  const decryptedToken: any = jwtDecode(token);
  try {
    return decryptedToken?.isProjectMember;
  } catch (ex) {
    return false;
  }
};

const isProcoreProject = (projectIntegrationConfig: any) => {
  const systemIntgConfig =
    projectIntegrationConfig &&
    projectIntegrationConfig.length > 0 &&
    projectIntegrationConfig[0]?.systemIntgConfigProperties;

  return systemIntgConfig?.DATA_SOURCE_NAME === "Procore";
};

const getUserClaimsFromToken = (token: string) => {
  const hasuraClaims = getHasuraClaims(token);
  return hasuraClaims;
};

export const isPermissionNotGrantted = isPermissionNotGranttedUtil;

function ProjectProvider({ children }: ProjectProviderProps) {
  const [projectPermissionAccess, setProjectPermissionAccess] = useState(true);
  const [projectState, setProjectState] = useState<TProjectContext>({
    projectToken: null,
    gqlClientForProject: null,
    tokenRetrievalState: null,
    columnHeaders: {
      submittalHeaders: null,
      materialHeaders: null,
      submittalHeaderMap: {},
      materialHeaderMap: {},
      bidPackageHeaders: null,
      designPackageHeaders: null,
      bidPackageHeaderMap: {},
      designPackageHeaderMap: {}
    },
    projectDetails: undefined,
    projectIntegrationConfig: null,
    eventLogs: [],
    lastImportLogData: null,
    isFeatureFlagEnabled: () => false,
    isProductEntitlementsDataAvailable: false,
    isProjectMember: false,
    isProcoreProject: false,
    loggedInUserProjectProfileResponse: {
      loading: true,
      error: null,
      data: undefined
    },
    metadata: {}
  });
  const { projectId } = useParams() as any;

  const { gqlClientForProject, tokenRetrievalState, projectToken } =
    useGetGQLClientForProject(projectId);

  const { data: eventLogs } = useChangeEventPolling({ gqlClientForProject });

  const {
    projectDetails: projectDetailsData,
    calendarOffs,
    columnHeaders,
    projectIntgConfigs,
    lastImportStatus,
    isFeatureFlagEnabled,
    isProductEntitlementsDataAvailable
  } = useProjectGlobalDataSource(eventLogs, gqlClientForProject, { projectId });

  const {
    data: loggedInUserProjectProfileData,
    loading: loggedInUserProjectProfileLoading,
    error: loggedInUserProjectProfileError
  } = useUserProjectProfileInfo(gqlClientForProject);

  // Effect 1: Sync data to state (eventLogs, lastImportStatus, projectIntegrationConfig)
  useEffect(() => {
    const integrationConfig = simplifyProjectIntegrationConfigs(
      projectIntgConfigs?.project_integration_configs
    );

    setProjectState((prev: TProjectContext) => ({
      ...prev,
      eventLogs,
      lastImportLogData: lastImportStatus,
      projectIntegrationConfig: integrationConfig,
      isProcoreProject: isProcoreProject(integrationConfig)
    }));
  }, [
    eventLogs,
    lastImportStatus,
    projectIntgConfigs?.project_integration_configs
  ]);

  // Effect 2: Handle project details and timezone
  useEffect(() => {
    if (projectDetailsData && projectDetailsData.project_by_pk) {
      try {
        moment.tz.setDefault(
          projectDetailsData.project_by_pk.timezone.timezone_id
        );
      } catch (err) {
        console.log(err);
        message.error(ErrorMessages.TimezoneFetchFailed);
      }
      setProjectState((pre) => ({
        ...pre,
        projectDetails: projectDetailsData.project_by_pk
      }));
    }
  }, [projectDetailsData]);

  // Effect 3: Token and authentication handling
  useEffect(() => {
    if (tokenRetrievalState.error === true) {
      setProjectPermissionAccess(false);
    }
    if (
      projectId &&
      gqlClientForProject &&
      tokenRetrievalState.success &&
      tokenRetrievalState.token
    ) {
      setProjectPermissionAccess(true);
      setProjectState((pre) => ({
        ...pre,
        projectToken,
        gqlClientForProject,
        tokenRetrievalState,
        tokenContents: {
          userType: getUserTypeFromToken(tokenRetrievalState.token),
          role: getProjectRoleFromToken(tokenRetrievalState.token),
          claims: getUserClaimsFromToken(tokenRetrievalState.token)
        },
        calHolidaysWeekends: calendarOffs,
        columnHeaders,
        isFeatureFlagEnabled,
        isProductEntitlementsDataAvailable,
        isProjectMember: isCurrentProjectMember(tokenRetrievalState.token),
        loggedInUserProjectProfileResponse: {
          data: loggedInUserProjectProfileData,
          loading: loggedInUserProjectProfileLoading,
          error: loggedInUserProjectProfileError
        }
      }));

      (window as any).projectToken = tokenRetrievalState.token; // for dev purposes
    }
  }, [
    projectToken,
    projectId,
    gqlClientForProject,
    tokenRetrievalState,
    calendarOffs,
    columnHeaders,
    isFeatureFlagEnabled,
    isProductEntitlementsDataAvailable,
    loggedInUserProjectProfileData,
    loggedInUserProjectProfileLoading,
    loggedInUserProjectProfileError
  ]);

  return (
    <ProjectContext.Provider value={projectState}>
      {projectPermissionAccess ? (
        children
      ) : (
        <NotFoundPage
          status="403"
          title="403"
          subTitle="Sorry, you are not authorized to access this page."
        />
      )}
    </ProjectContext.Provider>
  );
}

export default ProjectProvider;
